// The settings screens: one list of items, no hierarchy (inside the desktop
// app, the items of this app stand apart under their heading). The sidebar is the
// settings sidebar every settings screen uses (`shell/settings-sidebar.tsx`);
// the index lists every item with one sentence; an item's page has the item's
// name as its title (from the same definition as the sidebar, so they never
// differ), one sentence, and boxes of rows (name and note on the left, the
// control on the right). A workspace's settings use the same page, sections,
// boxes and rows.
//
// Saving: no save buttons. A change is sent on blur / Enter / choosing; nothing
// is sent when nothing changed; only a failure shows a line.
import { Link, useRouterState } from "@tanstack/react-router";
import { m } from "@trameria/i18n";
import type { ReactNode } from "react";
import { useMeApp } from "../app/context";
import type { SettingsItem } from "../app/screen-part";
import { desktopShell } from "../device/desktop-shell";
import { useLeave } from "../shell/list-memory";
import { useShellEmbedded } from "../shell/outside/outside";
import { DrawerButton, PanelBody } from "../shell/panel";
import { DockUp, PhoneTopDock, usePhone } from "../shell/phone";
import { SettingsSidebar as Sidebar } from "../shell/settings-sidebar";
import { Icon, iconOf } from "../ui/icon";

const useTitles = () => {
  const { extensions } = useMeApp();
  return {
    title: extensions.settingsTitle?.() ?? m.shell_settings(),
    description:
      extensions.settingsDescription?.() ?? m.shell_settings_description(),
  };
};

/** The settings items this person sees (an item may show only to some). */
function useShownSettings(): readonly SettingsItem[] {
  const { settings } = useMeApp();
  // The items are fixed for the app's life, so the same hooks run in the same
  // order on every render.
  // biome-ignore lint/correctness/useHookAtTopLevel: one hook per fixed item, always in the same order
  const shown = settings.map((item) => item.useShown?.() ?? true);
  return settings.filter((_, i) => shown[i]);
}

/** An entry of the sidebar and the index: an item's page, or a row that opens the app's settings window. */
interface SettingsEntry {
  id: string;
  label(): string;
  icon: SettingsItem["icon"];
  description(): string;
  sections?: SettingsItem["sections"];
  open?(): void;
}

const entryOf = (item: SettingsItem): SettingsEntry => ({
  id: item.id,
  label: item.label,
  icon: item.icon,
  description: item.description,
  sections: item.sections,
});

const AppSettingsIcon = iconOf("settings");

/**
 * The shown items in their groups: inside the desktop app the app's items
 * under "This app" after the rest; elsewhere one group without a heading.
 * Where the app's items live in a window of their own (`appSettings`), one
 * row under "This app" opens it; in that window (`settingsWindow`), only the
 * app's items.
 */
function useSettingsGroups(): ReadonlyArray<{
  label?(): string;
  items: readonly SettingsEntry[];
}> {
  const { extensions } = useMeApp();
  const settings = useShownSettings();
  const app = settings.filter((item) => item.group === "app");
  const rest = settings.filter((item) => item.group !== "app");
  if (extensions.settingsWindow) return [{ items: app.map(entryOf) }];
  const opener = extensions.appSettings;
  if (opener)
    return [
      ...(rest.length > 0 ? [{ items: rest.map(entryOf) }] : []),
      {
        label: () => m.settings_group_app(),
        items: [
          {
            id: "app-settings",
            label: () => m.settings_app_open(),
            icon: AppSettingsIcon,
            description:
              opener.description ?? (() => m.settings_app_open_description()),
            open: () => opener.open(),
          },
        ],
      },
    ];
  if (desktopShell() === null || app.length === 0)
    return [{ items: settings.map(entryOf) }];
  return [
    ...(rest.length > 0 ? [{ items: rest.map(entryOf) }] : []),
    { label: () => m.settings_group_app(), items: app.map(entryOf) },
  ];
}

/** `folded`: the same sidebar folded to its icons. */
export function SettingsSidebar({ folded = false }: { folded?: boolean }) {
  const groups = useSettingsGroups();
  const { title } = useTitles();
  const { extensions } = useMeApp();
  const leave = useLeave("/me/settings", "/me");
  const settingsWindow = extensions.settingsWindow;
  return (
    <Sidebar
      label={title}
      title={title}
      indexTo="/me/settings"
      onBack={settingsWindow ? () => settingsWindow.close() : leave}
      folded={folded}
      groups={groups.map((g) => ({
        label: g.label,
        items: g.items.map((item) => ({
          ...item,
          to: `/me/settings/${item.id}`,
        })),
      }))}
    />
  );
}

/**
 * A settings page: the drawer's button where the sidebar is a drawer, then the
 * title said once (h1, 22 px, not bold), a sentence, and the rest.
 */
export function SettingsPage({
  title,
  description,
  wide = false,
  children,
}: {
  title: ReactNode;
  description?: ReactNode;
  /** Tables use the whole width; boxes of rows keep a reading width. */
  wide?: boolean;
  children?: ReactNode;
}) {
  return (
    <>
      <DrawerButton />
      <SettingsTopDock />
      <PanelBody cornerClear>
        <div className={`@container ${wide ? "" : "max-w-[640px]"}`}>
          <SettingsTitle title={title} description={description} />
          {children}
        </div>
      </PanelBody>
    </>
  );
}

/**
 * Inside an app on a phone, the app draws a way back at the top of a settings
 * screen when there is a page before it (it lay over the title, 2026-10-30):
 * an empty Dock at the top keeps its room (phone.css), as a list's does. A
 * browser shows none (the settings' round button is their way).
 */
export function SettingsTopDock() {
  const phone = usePhone();
  const embedded = useShellEmbedded();
  if (!phone || !embedded) return null;
  return <PhoneTopDock back />;
}

/** The title and sentence of a settings page (also over a settings list). */
export function SettingsTitle({
  title,
  description,
}: {
  title: ReactNode;
  description?: ReactNode;
}) {
  return (
    <>
      <h1 className="text-[22px] font-normal tracking-[-0.01em] text-fg">
        {title}
      </h1>
      {description && (
        <p className="mt-[7px] text-[12px] leading-[1.7] text-faint">
          {description}
        </p>
      )}
    </>
  );
}

/**
 * A heading over a part of a settings page (over a box of rows, a table): 11 px,
 * spaced, faint, never bold — the same as the sidebar's group headings.
 */
export function SettingsSection({
  id,
  label,
  end,
  children,
}: {
  /** The anchor the settings search opens (`#<id>`). */
  id?: string;
  label: ReactNode;
  /** At the right end of the heading's line (a count, a "+"). */
  end?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <section id={id} className="mt-7 scroll-mt-4">
      <h2 className="flex min-h-[18px] items-center gap-2 text-[11px] font-normal tracking-[0.04em] text-faint">
        <span className="min-w-0 flex-1">{label}</span>
        {end}
      </h2>
      {children}
    </section>
  );
}

/** One entry of a settings index. */
export interface SettingsIndexEntry {
  id: string;
  to: string;
  label(): string;
  description(): string;
  /** Its icon (the sidebar's), drawn on a phone's index. */
  icon?: SettingsItem["icon"];
  /** A row that opens something else (the app's settings window) instead of a page. */
  open?(): void;
}

/**
 * The index of settings: every item with its sentence, under the groups'
 * headings (the old web's: what each item changes, before choosing one). On
 * a phone, where there is no sidebar, it is the way between the items: a box
 * of rows with the items' icons, each going into its page.
 */
export function SettingsIndexPage(props: {
  title: ReactNode;
  description?: ReactNode;
  groups: ReadonlyArray<{
    label?(): string;
    items: readonly SettingsIndexEntry[];
  }>;
}) {
  const phone = usePhone();
  return phone ? (
    <PhoneSettingsIndex {...props} />
  ) : (
    <WideSettingsIndex {...props} />
  );
}

function WideSettingsIndex({
  title,
  description,
  groups,
}: {
  title: ReactNode;
  description?: ReactNode;
  groups: ReadonlyArray<{
    label?(): string;
    items: readonly SettingsIndexEntry[];
  }>;
}) {
  return (
    <SettingsPage title={title} description={description}>
      {groups.map((g, i) => (
        <section key={g.label?.() ?? i} className="mt-5">
          {g.label && (
            <h2 className="text-[11px] font-normal tracking-[0.04em] text-faint">
              {g.label()}
            </h2>
          )}
          <div className="mt-1 flex flex-col">
            {g.items.map((item) => {
              const body = (
                <>
                  <div className="text-[13px] text-fg">{item.label()}</div>
                  <div className="mt-[2px] text-[12px] leading-[1.6] text-faint">
                    {item.description()}
                  </div>
                </>
              );
              const open = item.open;
              return open ? (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => open()}
                  className="-mx-2 cursor-pointer rounded-[6px] px-2 py-2 text-left hover:bg-hover"
                >
                  {body}
                </button>
              ) : (
                <Link
                  key={item.id}
                  to={item.to}
                  className="-mx-2 rounded-[6px] px-2 py-2 hover:bg-hover"
                >
                  {body}
                </Link>
              );
            })}
          </div>
        </section>
      ))}
    </SettingsPage>
  );
}

const PHONE_ROW =
  "flex w-full items-center gap-3 px-4 py-3 text-left first:rounded-t-[10px] last:rounded-b-[10px] active:bg-hover";

/**
 * A phone's index (the iOS Settings app's): each group's rows in a box, the
 * item's icon, its name and sentence, and a chevron into its page.
 */
function PhoneSettingsIndex({
  title,
  description,
  groups,
}: {
  title: ReactNode;
  description?: ReactNode;
  groups: ReadonlyArray<{
    label?(): string;
    items: readonly SettingsIndexEntry[];
  }>;
}) {
  return (
    <SettingsPage title={title} description={description}>
      {groups.map((g, i) => (
        <section key={g.label?.() ?? i} className="mt-7">
          {g.label && (
            <h2 className="text-[11px] font-normal tracking-[0.04em] text-faint">
              {g.label()}
            </h2>
          )}
          <div className="mt-2 flex flex-col divide-y divide-border-soft rounded-[10px] border border-panel-border">
            {g.items.map((item) => {
              const ItemIcon = item.icon;
              const body = (
                <>
                  {ItemIcon && <ItemIcon className="flex-none text-muted" />}
                  <span className="min-w-0 flex-1">
                    <span className="block text-[13px] leading-[1.5] text-fg">
                      {item.label()}
                    </span>
                    <span className="mt-px block text-[11.5px] leading-[1.5] text-faint">
                      {item.description()}
                    </span>
                  </span>
                  <Icon kind="forward" className="flex-none text-faint" />
                </>
              );
              const open = item.open;
              return open ? (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => open()}
                  className={`${PHONE_ROW} cursor-pointer`}
                >
                  {body}
                </button>
              ) : (
                <Link key={item.id} to={item.to} className={PHONE_ROW}>
                  {body}
                </Link>
              );
            })}
          </div>
        </section>
      ))}
    </SettingsPage>
  );
}

/**
 * On a phone, while an item's page is open (anything under `index`), the
 * Dock's left slot goes up to the index (there is no sidebar to choose from).
 * Put it before the frame's pages: a list's own way up to itself (a detail
 * alone) comes after it and wins.
 */
export function SettingsDockUp({ index }: { index: string }) {
  const pathname = useRouterState({ select: (r) => r.location.pathname });
  const atIndex = pathname.replace(/\/+$/, "") === index;
  if (atIndex || !pathname.startsWith(`${index}/`)) return null;
  return <DockUp to={index} label={m.settings_back_to_index()} />;
}

export function SettingsIndex() {
  const groups = useSettingsGroups();
  const { title, description } = useTitles();
  return (
    <SettingsIndexPage
      title={title}
      description={description}
      groups={groups.map((g) => ({
        label: g.label,
        items: g.items.map((item) => ({
          id: item.id,
          to: `/me/settings/${item.id}`,
          label: item.label,
          description: item.description,
          icon: item.icon,
          open: item.open,
        })),
      }))}
    />
  );
}

export function SettingsItemPage({ item }: { item: SettingsItem }) {
  const Content = item.Component;
  // No header row: the page's title is the item's name, said once.
  return (
    <SettingsPage title={item.label()} description={item.description()}>
      <Content />
    </SettingsPage>
  );
}

/** A box of rows. Boxes hold rows; sections and forms are not boxed. */
export function SettingsBox({ children }: { children: ReactNode }) {
  return (
    <div className="mt-4 divide-y divide-border-soft rounded-[10px] border border-panel-border">
      {children}
    </div>
  );
}

/** A row: name and note on the left, the control on the right (stacked when narrow). */
export function SettingsRow({
  id,
  label,
  description,
  children,
}: {
  id?: string;
  label: ReactNode;
  description?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div id={id} className="@container scroll-mt-4 px-4 py-3">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <div className="min-w-[160px] flex-1 [word-break:auto-phrase]">
          <div className="text-[13px] leading-[1.5] text-fg">{label}</div>
          {description && (
            <div className="mt-px text-[11.5px] leading-[1.5] text-faint">
              {description}
            </div>
          )}
        </div>
        <div className="flex w-full min-w-0 justify-end *:min-w-0 *:max-w-full @min-[440px]:w-[260px]">
          {children}
        </div>
      </div>
    </div>
  );
}
